import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, tokenStore } from '../api/client';
import type { CurrentUser, Permission } from '../types/domain';

interface LoginInput { username: string; password: string }

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  login: (input: LoginInput) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  can: (permission?: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const permissionCode = (permission: string | Permission) =>
  typeof permission === 'string' ? permission : permission.code;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const next = await api.get<CurrentUser>('/auth/me');
      setUser({ ...next, permissions: next.permissions ?? [] });
    } catch {
      setUser(null);
      tokenStore.set(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    const unauthorized = () => setUser(null);
    window.addEventListener('shmh:unauthorized', unauthorized);
    return () => window.removeEventListener('shmh:unauthorized', unauthorized);
  }, [reload]);

  const login = useCallback(async (input: LoginInput) => {
    const result = await api.post<{ accessToken?: string; user?: CurrentUser } & CurrentUser>('/auth/login', input, { skipRefresh: true });
    tokenStore.set(result.accessToken);
    const next = result.user ?? (result.id ? result : await api.get<CurrentUser>('/auth/me'));
    setUser({ ...next, permissions: next.permissions ?? [] });
    return next;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } finally {
      tokenStore.set(null);
      setUser(null);
    }
  }, []);

  const can = useCallback((permission?: string) => {
    if (!permission) return true;
    if (!user) return false;
    if (user.systemRole === 'SUPER_ADMIN' || user.systemRole === 'ADMIN') return true;
    const granted = new Set(user.permissions.map(permissionCode));
    return granted.has('*') || granted.has(permission);
  }, [user]);

  const value = useMemo(() => ({ user, loading, login, logout, reload, can }), [user, loading, login, logout, reload, can]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
};
